"use client";

import React from "react";
import { ArrowRight, BookOpen, ChevronLeft, ChevronRight, LoaderCircle } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div data-section-id="233771" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/50 shadow-sm">
            <div className="aspect-[16/10] w-full animate-pulse bg-muted" />
            <CardHeader className="space-y-3">
              <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
              <div className="h-6 w-3/4 animate-pulse rounded bg-muted" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-4 w-full animate-pulse rounded bg-muted" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border p-12 text-center">
        <p className="mb-4 text-muted-foreground">Impossibile caricare i racconti della montagna.</p>
        <Button onClick={refetch} variant="outline" className="border-primary text-primary hover:bg-primary/10">
          Riprova
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border p-12 text-center">
        <BookOpen className="mb-4 size-12 text-muted-foreground/50" />
        <h3 className="mb-2 text-xl font-semibold font-default">Nessun Capitolo Trovato</h3>
        <p className="text-muted-foreground">Il diario di viaggio è ancora in fase di scrittura.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border/30 bg-card shadow-sm transition-all duration-500 hover:shadow-xl hover:border-primary/30">
            <Link to={post.link || "#"} className="block overflow-hidden relative">
              <div className="aspect-[16/10] w-full overflow-hidden bg-muted">
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648136/img/handpan_mountain_summit.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                   <div className="rounded-full bg-primary/80 p-4 text-primary-foreground shadow-lg shadow-primary/20">
                      <BookOpen className="size-6" />
                   </div>
                </div>
              </div>
            </Link>
            <CardHeader className="flex-none pb-4">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-medium font-sans text-muted-foreground">
                {post.categories && post.categories.length > 0 && <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-primary uppercase tracking-wider">
                    {post.categories[0].name}
                  </span>}
                {post.date && <>
                    <span className="text-muted-foreground/50">•</span>
                    <span className="font-mono">
                      {new Date(post.date).toLocaleDateString("it-IT", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
                    </span>
                  </>}
              </div>
              <Link to={post.link || "#"} className="block">
                <CardTitle className="line-clamp-2 text-2xl font-default leading-tight transition-colors group-hover:text-primary">
                  {post.title}
                </CardTitle>
              </Link>
            </CardHeader>
            <CardContent className="flex-grow">
              <CardDescription className="line-clamp-3 text-base font-serif leading-relaxed text-muted-foreground">
                {post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "Un viaggio tra le vette del Vicentino, dove il suono dell'handpan incontra il silenzio sacro della storia."}
              </CardDescription>
            </CardContent>
            <CardFooter className="flex-none pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="ghost" className="group/btn w-full justify-between px-0 hover:bg-transparent hover:text-primary">
                  <span className="font-semibold font-sans uppercase tracking-widest text-xs">Leggi il Capitolo</span>
                  <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-2" />
                </Button>
              </Link>
            </CardFooter>
          </Card>)}
      </div>

      {totalPages > 1 && <div className="flex items-center justify-center gap-4 pt-8">
          <Button variant="outline" size="icon" onClick={prevPage} disabled={!hasPrev} aria-label="Pagina precedente" className="rounded-full border-border hover:border-primary hover:text-primary">
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-sm font-medium font-sans text-muted-foreground">
            Capitolo {currentPage} di {totalPages}
          </span>
          <Button variant="outline" size="icon" onClick={nextPage} disabled={!hasNext} aria-label="Pagina successiva" className="rounded-full border-border hover:border-primary hover:text-primary">
            <ChevronRight className="size-4" />
          </Button>
        </div>}
    </div>;
}
export default function EyeHealthEssentials() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}